import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  CONSULTA_OSCURO,
  getTemaPreferido,
  resolverTema,
  setTemaPreferido,
  sistemaEnOscuro,
} from "../services/temaService";

/**
 * El tema visual de la aplicación.
 *
 * Guarda tres cosas distintas y conviene no confundirlas:
 *
 * - `preferencia`: lo que la persona eligió --«claro», «oscuro» o «sistema»--.
 *   Es lo que se guarda y lo que muestra el selector de Configuración.
 * - `tema`: lo que toca pintar ahora, ya resuelto. Nunca vale «sistema».
 * - `esOscuro`: lo mismo en booleano, que es lo que necesita el algoritmo de la
 *   librería de interfaz.
 *
 * El valor por omisión del contexto es el tema claro y un `cambiarTema` que no
 * hace nada, para que un componente montado fuera del proveedor --una prueba
 * unitaria, por ejemplo-- se dibuje en vez de reventar.
 */
const TemaContext = createContext({
  preferencia: "claro",
  tema: "claro",
  esOscuro: false,
  cambiarTema: () => {},
});

export const ProveedorDeTema = ({ children }) => {
  const [preferencia, setPreferencia] = useState(getTemaPreferido);
  const [sistemaOscuro, setSistemaOscuro] = useState(sistemaEnOscuro);

  // El equipo puede cambiar de tema con el terminal abierto --Windows lo hace
  // solo al anochecer si está configurado así--, y un turno dura horas. Sin
  // escuchar el cambio, quien eligió «sistema» se queda con el tema que hubiera
  // al arrancar hasta que recargue.
  useEffect(() => {
    const consulta = window.matchMedia?.(CONSULTA_OSCURO);
    if (!consulta?.addEventListener) return undefined;

    const alCambiar = (evento) => setSistemaOscuro(evento.matches);
    consulta.addEventListener("change", alCambiar);
    return () => consulta.removeEventListener("change", alCambiar);
  }, []);

  const tema = resolverTema(preferencia, sistemaOscuro);

  // Marcar el documento, no sólo los componentes de la librería.
  //
  // El `ConfigProvider` pinta lo que dibuja la librería, pero el fondo de la
  // página lo pinta el navegador: sin esto quedan franjas blancas alrededor del
  // contenido. Y `color-scheme` es lo que pone en oscuro lo que no controlamos
  // --barras de desplazamiento, selectores de fecha nativos, autocompletado--.
  useEffect(() => {
    document.documentElement.dataset.tema = tema;
    document.documentElement.style.colorScheme = tema === "oscuro" ? "dark" : "light";
  }, [tema]);

  const cambiarTema = useCallback((nueva) => {
    setTemaPreferido(nueva);
    setPreferencia(nueva);
  }, []);

  const valor = useMemo(
    () => ({ preferencia, tema, esOscuro: tema === "oscuro", cambiarTema }),
    [preferencia, tema, cambiarTema]
  );

  return <TemaContext.Provider value={valor}>{children}</TemaContext.Provider>;
};

export const useTema = () => useContext(TemaContext);
